import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Suspense, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useKeyboardOpen } from '../hooks/useKeyboardOpen';
import { useMotion } from '../hooks/useMotion';
import { useChangeMarks } from '../hooks/useChangeMarks';
import { usePresence } from '../hooks/usePresence';
import { usePwaUpdate } from '../hooks/usePwaUpdate';
import { useScrimClose } from '../hooks/useScrimClose';
import { useSessionGuardian } from '../hooks/useSessionGuardian';
import { useTheme } from '../hooks/useTheme';
import { useWaiting } from '../hooks/useWaiting';
import { useShortcuts } from '../hooks/useShortcuts';
import { useSidebarRail } from '../hooks/useSidebarRail';
import { api } from '../lib/api';
import { PAGE_WIDTH_CLASS, pageWidthFor } from '../lib/layout';
import { navigationFor } from '../lib/navigation';
import { isReleaseNewer } from '../lib/release';
import { countUnseenWaiting } from '../lib/waiting';
import type { ProjectWorker } from '../lib/types';
import { formatDateTime } from '../lib/time';
import { lazyComponent } from '../lib/lazyPage';
import { ActivityBell } from './ActivityBell';
import { Avatar } from './Avatar';
import { BRAND_LOGO, GreetingOverlay } from './GreetingOverlay';
import { InkLayer } from './InkLayer';
import { InstallPrompt } from './InstallPrompt';
import { RebrandNotice } from './RebrandNotice';
import { LiveBackground } from './LiveBackground';
import { OfflineBar } from './OfflineBar';
import { OfflineQueueBar } from './OfflineQueueBar';
import { useOfflineQueueSync } from '../hooks/useOfflineQueue';
import { MobileTabBar, MoreSheet } from './shell/MobileTabBar';
import { Sidebar } from './shell/Sidebar';

/**
 * Okná, ktoré sa otvárajú až na klepnutie, a profilové menu. Spolu s Headless
 * UI (ktoré tu okrem nich nikto nepotrebuje) to bola asi tretina hlavného
 * balíka — a ten musí prísť celý, kým sa vykreslí čokoľvek. Service worker ich
 * má v generácii dopredu, takže prvé otvorenie nečaká na sieť.
 */
const JarvisAssistant = lazyComponent(() => import('./JarvisAssistant'), 'JarvisAssistant');
const ProfileMenu = lazyComponent(() => import('./ProfileMenu'), 'ProfileMenu');
const SearchModal = lazyComponent(() => import('./SearchModal'), 'SearchModal');
const StatusSheet = lazyComponent(() => import('./StatusSheet'), 'StatusSheet');
const StopSessionModal = lazyComponent(() => import('./StopSessionModal'), 'StopSessionModal');

// Farby z tokenov bočného panela: vo vzhľade Nočná smena je panel tmavý aj
// vo svetlom režime, takže text-slate-* podľa .dark by tam bol nečitateľný.
const PROFILE_BUTTON_CLASS = 'sidebar-profile';

/**
 * Lenivé okno sa pripojí až pri prvom otvorení a potom už ostane. Keby sa
 * odpájalo pri každom zatvorení, asistent by zabudol rozhovor a okná by
 * nestihli dohrať animáciu zatvárania.
 */
const useMountedOnce = (open: boolean): boolean => {
  const [mounted, setMounted] = useState(open);
  if (open && !mounted) setMounted(true);
  return mounted || open;
};

// One image for the whole lockup — the artwork already carries both the mark
// and the word WORKS, so nothing here re-types the brand name.

const WordMark = () => (
  <Link to="/" className="flex items-center">
    <img src={BRAND_LOGO} alt="ONVI WORKS" className="h-[22px] w-auto shrink-0" />
  </Link>
);

export const AppShell = ({ children }: { children: React.ReactNode }) => {
  const { user, streakDays, logout, generalUnread } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { updateAvailable } = usePwaUpdate();
  const [moreOpen, setMoreOpen] = useState(false);
  const { rail, toggleRail } = useSidebarRail();
  const [guardianStopOpen, setGuardianStopOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchSeed, setSearchSeed] = useState('');
  const [jarvisOpen, setJarvisOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const searchMounted = useMountedOnce(searchOpen);
  const jarvisMounted = useMountedOnce(jarvisOpen);
  const statusMounted = useMountedOnce(statusOpen);
  const keyboardOpen = useKeyboardOpen();
  const { animates } = useMotion();
  // Hosť (pozvaný do jedného projektu) má užší rám: bez tímu, JARVISa a
  // všeobecných prehľadov. API by mu ich aj tak nevydalo (404), takže sa na
  // ne ani nepýtame — lib/navigation.ts.
  const guest = user?.role === 'guest';
  const navigation = navigationFor(user?.role);

  // Odznak schránky. Vlastný dopyt, nie súčasť `me` — počet sa mení nezávisle od
  // prihláseného človeka a `me` sa nemá obnovovať každú minútu.
  const inboxQuery = useQuery({
    queryKey: ['inbox', 'unread'],
    queryFn: () => api.inboxList(true),
    enabled: Boolean(user),
    refetchInterval: 60_000,
  });
  // K neprečítaným push riadkom sa pripočíta nové z „Čaká na mňa“ — tichý
  // osobný ukazovateľ z T-37/T-29. Čo už má svoj push riadok, sa neráta
  // dvakrát (pozri countUnseenWaiting).
  const waiting = useWaiting(user?.id);
  const inboxUnread =
    (inboxQuery.data?.unread ?? 0) +
    countUnseenWaiting(waiting.items, waiting.seenAt, inboxQuery.data?.items);

  const appReleaseQuery = useQuery({
    queryKey: ['app-release', 'current'],
    queryFn: () => api.appReleaseCurrent(),
    enabled: Boolean(user),
    staleTime: 60 * 60_000,
    refetchInterval: 60 * 60_000,
  });
  const advertisedVersion = appReleaseQuery.data?.release.version;
  // The release manifest is authoritative when reachable. This prevents an
  // old/stale waiting worker with the same semantic version from leaving a
  // badge that cannot be cleared; service-worker detection remains the
  // offline fallback.
  const hasAppUpdate = appReleaseQuery.data
    ? Boolean(advertisedVersion && isReleaseNewer(advertisedVersion))
    : updateAvailable;

  usePresence(Boolean(user));
  // Jeden lacný dopyt „pribudlo niečo?" namiesto toho, aby sa každá obrazovka
  // pýtala na svoje drahé dáta dookola. Drahé dotazy si nechávajú vlastný,
  // pomalší interval ako záchytnú sieť pre úpravy, ktoré známka nezachytí.
  useChangeMarks(Boolean(user));

  const openSearch = (query = '') => {
    setSearchSeed(query);
    setSearchOpen(true);
  };

  // C, /, G+P/D/I/T a ? — pravidlá (a hlavne kedy sa skratka nespustí) sú v lib/shortcuts.ts.
  const shortcutsHelp = useShortcuts({ openSearch: () => openSearch(), toggleSidebar: toggleRail });
  // Odložené zmeny spred výpadku siete (lib/offlineQueue.ts) sa odošlú, keď sa dá.
  useOfflineQueueSync(user?.id ?? null);

  // Global Cmd/Ctrl+K opens search; Cmd/Ctrl+J wakes JARVIS.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchSeed('');
        setSearchOpen(true);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j' && !guest) {
        e.preventDefault();
        setJarvisOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [guest]);

  const projectsQuery = useQuery({
    queryKey: ['projects', 'list', false],
    queryFn: () => api.projectsList(false),
    enabled: Boolean(user),
    staleTime: 15_000,
  });
  // Projects whose timers I am running — a person may work on several at once
  // (migration 0043). The app wears the colour of the one started last, which
  // is the one just switched to.
  const myRunningProjects = useMemo(() => {
    const mine = (projectsQuery.data?.items ?? [])
      .map((project) => ({
        project,
        worker: project.workers?.find((worker) => worker.userId === user?.id) ?? null,
      }))
      .filter((entry): entry is { project: (typeof entry)['project']; worker: ProjectWorker } =>
        Boolean(entry.worker),
      );
    return mine.sort((a, b) => b.worker.startedAt.localeCompare(a.worker.startedAt));
  }, [projectsQuery.data?.items, user?.id]);

  const myRunningProject = myRunningProjects[0]?.project ?? null;

  useEffect(() => {
    const root = document.documentElement;
    if (myRunningProject && animates) {
      root.style.setProperty('--ww-session-tint', myRunningProject.color);
      root.classList.add('session-live');
    } else {
      root.classList.remove('session-live');
    }
    return () => root.classList.remove('session-live');
  }, [myRunningProject, animates]);

  const rosterQuery = useQuery({
    queryKey: ['users', 'list'],
    queryFn: () => api.usersList(),
    enabled: Boolean(user),
    staleTime: 15_000,
  });
  const dmUnreadQuery = useQuery({
    queryKey: ['dm', 'unread'],
    queryFn: () => api.dmUnread(),
    enabled: Boolean(user),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  // Close the "Viac" sheet whenever the route changes.
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  // Git pushes (GitHub webhooks): summarize what landed since this device last
  // looked, so you know to `git pull`. The single git notification in the app.
  const gitRealQuery = useQuery({
    queryKey: ['events', 'git-push-real'],
    queryFn: () => api.eventsList({ types: 'git.push', limit: 10 }),
    // „Sprav git pull" je pre vývojárov z tímu, hosť repozitár nemá.
    enabled: Boolean(user) && !guest,
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    const items = (gitRealQuery.data?.items ?? []).filter((it) => it.actorUserId !== user?.id);
    if (items.length === 0) {
      return;
    }
    const seenKey = 'ww_seen_git_push_real';
    const lastSeen = Number(localStorage.getItem(seenKey) || 0);
    const newest = items[0].id;
    const fresh = items.filter((it) => it.id > lastSeen);
    if (fresh.length > 0 && lastSeen > 0) {
      // Group fresh pushes per project into a single summary toast.
      const byProject = new Map<string, { commits: number; actors: Set<string> }>();
      for (const it of fresh) {
        const key = it.projectName ?? 'Bez projektu';
        const entry = byProject.get(key) ?? { commits: 0, actors: new Set<string>() };
        entry.commits += Number(it.payload.commitCount) || 0;
        const actor = it.actorName ?? (it.payload.actor as string | undefined);
        if (actor) {
          entry.actors.add(actor);
        }
        byProject.set(key, entry);
      }
      const plural = (n: number) => (n === 1 ? 'commit' : n < 5 ? 'commity' : 'commitov');
      const lines = Array.from(byProject.entries()).map(([projectName, v]) => {
        const who = v.actors.size > 0 ? ` od ${Array.from(v.actors).join(', ')}` : '';
        return `${projectName}: ${v.commits} ${plural(v.commits)}${who}`;
      });
      toast(`Nové commity — sprav git pull. ${lines.join(' · ')}`, {
        duration: 9000,
        id: 'git-pull-reminder',
      });
    }
    if (newest > lastSeen) {
      localStorage.setItem(seenKey, String(newest));
    }
  }, [gitRealQuery.data, user?.id]);

  const projects = (projectsQuery.data?.items ?? []).filter((p) => !p.archived);

  // Live projects first, then the ones with unread chat, then the rest — with
  // six projects the alphabet stops being a useful order.
  const sidebarProjects = useMemo(() => {
    const score = (project: (typeof projects)[number]) =>
      (project.workers.length > 0 ? 0 : 2) + (project.unreadMessages > 0 ? -1 : 0);
    return [...projects].sort((a, b) => score(a) - score(b) || a.name.localeCompare(b.name, 'sk'));
  }, [projects]);
  const me = rosterQuery.data?.items.find((u) => u.id === user?.id);
  const canCreateProjects = ['owner', 'admin'].includes(user?.role ?? '');

  // Total unread for the Chat destination: general channel + project channels + DMs.
  const chatUnread =
    (generalUnread || 0) +
    projects.reduce((n, p) => n + (p.unreadMessages || 0), 0) +
    (dmUnreadQuery.data?.items ?? []).reduce((n, x) => n + x.unread, 0);

  // Mirror the unread count onto the installed-PWA app icon (iOS 16.4+,
  // Android, desktop Chrome). No-op where the Badging API is missing.
  useEffect(() => {
    const nav = navigator as Navigator & {
      setAppBadge?: (count: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (chatUnread > 0) {
      void nav.setAppBadge?.(chatUnread).catch(() => undefined);
    } else {
      void nav.clearAppBadge?.().catch(() => undefined);
    }
  }, [chatUnread]);

  // Session guardian: if my session has been running while the tab was hidden
  // for a long time, prompt me to close it retroactively.
  // The guardian watches the session running the LONGEST — with several open at
  // once that is the one most likely to have been forgotten.
  const myActiveSession = useMemo(() => {
    const oldest = myRunningProjects[myRunningProjects.length - 1];
    return oldest
      ? {
          sessionId: oldest.worker.sessionId,
          projectId: oldest.project.id,
          projectName: oldest.project.name,
        }
      : null;
  }, [myRunningProjects]);

  const { prompt, clearPrompt } = useSessionGuardian(myActiveSession);

  const guardianScrim = useScrimClose(clearPrompt);

  const dismissGuardian = () => {
    setGuardianStopOpen(false);
    clearPrompt();
  };

  const profileFace = (
    <>
      <Avatar
        name={user?.name ?? '?'}
        src={me?.avatarUrl}
        size="sm"
        statusMode={me?.statusMode}
        statusColor={me?.statusColor}
        online={me?.online}
      />
      <span className="sidebar-label min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold" style={{ color: 'var(--ww-sidebar-fg-strong)' }}>
          {user?.name}
        </span>
        <span className="block truncate text-[11px]" style={{ color: 'var(--ww-sidebar-muted)' }}>
          {me?.statusText || `streak ${streakDays} d`}
        </span>
      </span>
    </>
  );
  const profileMenu = (
    <Suspense
      fallback={
        // Kým príde súbor s menu, rovnaké tlačidlo bez akcie — panel sa po
        // načítaní nesmie pohnúť. Trvá to zlomok sekundy a z cache workera.
        <div className="relative">
          <button type="button" className={PROFILE_BUTTON_CLASS} aria-disabled="true">
            {profileFace}
          </button>
        </div>
      }
    >
      <ProfileMenu
        face={profileFace}
        buttonClassName={PROFILE_BUTTON_CLASS}
        statusMode={me?.statusMode}
        onOpenStatus={() => setStatusOpen(true)}
      />
    </Suspense>
  );

  return (
    // minmax(0,1fr), nie 1fr: holý 1fr sa nezmenší pod šírku obsahu, takže
    // dlhý riadok záložiek projektu pri 768 px roztiahol celú stránku do šírky.
    <div
      className={`min-h-[100dvh] md:grid ${
        rail ? 'md:grid-cols-[68px_minmax(0,1fr)]' : 'md:grid-cols-[256px_minmax(0,1fr)]'
      }`}
    >
      {/* Static backdrop behind every screen — see LiveBackground.tsx. */}
      <LiveBackground />
      {/* Colour of the project being tracked, behind the whole app. */}
      <div className="session-tint" aria-hidden="true" />
      {/* Confirmation ink, above everything and deaf to the pointer. */}
      <InkLayer />
      {/* Pozdrav skladá deň z plánu a úloh celého človeka — hosť plán nemá. */}
      {guest ? null : <GreetingOverlay />}
      {/* Premenovanie z WhoWorks sa týka ľudí, ktorí appku poznali pod starým
          menom. Hosť ju vidí prvýkrát. */}
      {guest ? null : <RebrandNotice />}
      <InstallPrompt />

      <Sidebar
        rail={rail}
        onToggleRail={toggleRail}
        counts={{ chat: chatUnread, inbox: inboxUnread }}
        hasAppUpdate={hasAppUpdate}
        projects={sidebarProjects}
        myUserId={user?.id}
        canCreateProjects={canCreateProjects}
        onOpenSearch={() => openSearch()}
        onOpenJarvis={() => setJarvisOpen(true)}
        jarvisOpen={jarvisOpen}
        profile={profileMenu}
        bell={<ActivityBell className="sidebar-tool" />}
        navigation={navigation}
      />

      {/* Main column */}
      <div className="flex min-h-screen min-w-0 flex-col">
        {/* Horná lišta (telefón). Nepriehľadná, bez rozmazania — pozri .topbar. */}
        <header className="topbar sticky top-0 z-30 flex items-center justify-between border-b hairline px-4 py-2 pt-[max(0.5rem,env(safe-area-inset-top))] md:hidden">
          <WordMark />
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => openSearch()}
              aria-label="Hľadať"
              className="flex h-11 w-11 items-center justify-center rounded-full text-slate-500 transition hover:bg-black/[0.05] dark:text-slate-400 dark:hover:bg-white/10"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>
            <ActivityBell className="relative flex h-11 w-11 items-center justify-center rounded-full text-slate-500 transition hover:bg-black/[0.05] dark:text-slate-400 dark:hover:bg-white/10" />
            <button
              type="button"
              onClick={() => setStatusOpen(true)}
              aria-label="Môj stav"
              className="flex h-11 w-11 items-center justify-center rounded-full"
            >
              <Avatar
                name={user?.name ?? '?'}
                src={me?.avatarUrl}
                size="sm"
                statusMode={me?.statusMode}
                statusColor={me?.statusColor}
                online={me?.online}
              />
            </button>
          </div>
        </header>

        <OfflineBar />
        <OfflineQueueBar />

        <main
          className={`mx-auto w-full flex-1 px-4 pb-28 pt-5 md:px-8 md:pb-10 md:pt-7 xl:px-10 ${
            PAGE_WIDTH_CLASS[pageWidthFor(location.pathname, location.search)]
          }`}
        >
          {/* keyed by pathname so navigation gets a soft entrance transition */}
          <div key={location.pathname} className="tab-fade">
            {children}
          </div>
          <footer className="mt-12 border-t hairline pt-4 text-center text-[11px] leading-relaxed text-slate-400 dark:text-slate-500">
            Interný systém · vyvinuté spoločnosťou{' '}
            <span className="font-medium text-slate-500 dark:text-slate-400">ONVI s.r.o.</span>
          </footer>
        </main>
      </div>

      <MobileTabBar
        counts={{ chat: chatUnread, inbox: inboxUnread }}
        hasAppUpdate={hasAppUpdate}
        keyboardOpen={keyboardOpen}
        moreOpen={moreOpen}
        onOpenMore={() => setMoreOpen(true)}
        navigation={navigation}
      />

      {moreOpen ? (
        <MoreSheet
          counts={{ chat: chatUnread, inbox: inboxUnread }}
          hasAppUpdate={hasAppUpdate}
          theme={theme}
          onClose={() => setMoreOpen(false)}
          onToggleTheme={toggleTheme}
          onOpenJarvis={() => setJarvisOpen(true)}
          onLogout={() => void logout()}
          navigation={navigation}
        />
      ) : null}

      {/* Session guardian — prompt to retroactively close a session left running. */}
      {prompt && !guardianStopOpen ? (
        <div className="sheet-scrim" role="presentation" {...guardianScrim}>
          <div
            className="sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Vyzerá, že si bol preč"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-4 h-1.5 w-10 rounded-full bg-black/10 dark:bg-white/15 sm:hidden" />

            <h2 className="t-title">Vyzerá, že si bol preč</h2>
            <p className="mt-2 text-[15px] text-slate-500 dark:text-slate-400">
              Od {formatDateTime(prompt.awayFromIso)} si bol neaktívny a session na{' '}
              <span className="font-semibold text-slate-700 dark:text-slate-200">
                {prompt.projectName}
              </span>{' '}
              stále beží.
            </p>

            <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                className="btn-primary sm:order-2 sm:w-auto"
                type="button"
                onClick={() => setGuardianStopOpen(true)}
              >
                Ukončiť spätne
              </button>
              <button
                className="btn-secondary sm:order-1 sm:w-auto"
                type="button"
                onClick={clearPrompt}
              >
                Pokračujem v práci
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {prompt && guardianStopOpen ? (
        <Suspense fallback={null}>
          <StopSessionModal
            open={guardianStopOpen}
            onClose={() => setGuardianStopOpen(false)}
            loading={false}
            initialEndedAt={prompt.awayFromIso}
            onSubmit={async (payload) => {
              await api.sessionsStop({ sessionId: prompt.sessionId, ...payload });
              await queryClient.invalidateQueries({ queryKey: ['projects'] });
              dismissGuardian();
            }}
          />
        </Suspense>
      ) : null}

      {/* fallback={null}: okno sa ukáže, keď príde jeho kód. Celostránkový
          spinner by pri otvorení hľadania zbytočne zhasol aktuálnu stránku. */}
      {shortcutsHelp}
      {searchMounted ? (
        <Suspense fallback={null}>
          <SearchModal
            open={searchOpen}
            onClose={() => setSearchOpen(false)}
            initialQuery={searchSeed}
          />
        </Suspense>
      ) : null}
      {statusMounted ? (
        <Suspense fallback={null}>
          <StatusSheet open={statusOpen} onClose={() => setStatusOpen(false)} me={me} />
        </Suspense>
      ) : null}
      {/* Každé okno má vlastnú hranicu: kým sa sťahuje jedno, spoločná by
          skryla aj to, ktoré je už otvorené. */}
      {jarvisMounted && !guest ? (
        <Suspense fallback={null}>
          <JarvisAssistant
            open={jarvisOpen}
            onClose={() => setJarvisOpen(false)}
            userName={user?.name ?? 'používateľ'}
            projects={projects}
            chatUnread={chatUnread}
            onSearch={openSearch}
          />
        </Suspense>
      ) : null}
    </div>
  );
};
